import React, { useState, useEffect } from "react"
import Typography from "aldoo-ra/Typography"
import Button from "aldoo-ra/Button"
import DataTable from "aldoo-ra/DataTable"
import Tabs from "aldoo-ra/Tabs"
import Value from "aldoo-ra/Value"
import Checkbox from "aldoo-ra/Checkbox"
import ToggleSwitch from "aldoo-ra/ToggleSwitch"
import DropDownSelect from "aldoo-ra/DropdownSelect"
import { showNotification } from "aldoo-ra/Notification"
import { showPromptDialog } from "aldoo-ra/PromptDialog"
import { url } from "aldoo-ra/CMS/url"
import { showPrimaryPackConfirmDialog } from "./PrimaryPackConfirmDialog"
import {
  PencilIcon,
  PlayIcon,
  PauseIcon,
  TrashIcon,
  PlusCircleIcon,
  EyeIcon,
  ChartBarIcon,
  XMarkIcon,
  ArrowDownTrayIcon,
  ClipboardDocumentListIcon,
  DocumentTextIcon,
} from "@heroicons/react/24/outline"
import { GetPackDetails, GetLicenses, UpdatePack, DeletePack, DeleteLicenses } from "./api"
import AddPackDialog from "./AddPackDialog"
import LicenseExportDialog from "./LicenseExportDialog"
import UserPlatformAPI from "aldoo-ra/Admin/PlatformAccess/user-api"
import { navigate } from "vike/client/router"
import { twMerge } from "tailwind-merge"

export default function PackViewer({ packId, onClose }) {
  const [pack, setPack] = useState(null)
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState("details")
  const [refreshKey, setRefreshKey] = useState(0)
  const [editDialog, setEditDialog] = useState(false)
  const [selectedLicenses, setSelectedLicenses] = useState([])
  const [statusFilter, setStatusFilter] = useState([
    { value: null, label: "All Status" },
  ])
  const [userFilter, setUserFilter] = useState(null)
  const [urlParams, setUrlParams] = useState(new URLSearchParams())

  // Helper function to get user filter
  const getUserFilter = () => {
    if (!userFilter || !userFilter.length) return null
    const selectedUser = userFilter[0]
    return selectedUser._id
  }

  // Build request fields for license DataTable
  const getLicenseRequestFields = () => {
    const match = { licensePack: packId }

    // Add status filter if specified
    const selectedStatus = statusFilter[0]?.value
    if (selectedStatus) {
      match.status = selectedStatus
    }

    // Add user filter if specified
    const userFilterValue = getUserFilter()
    if (userFilterValue !== null) {
      match.owner = userFilterValue
    }

    return { 
      match,
      populate: ["owner"]
    }
  }

  // Handle status filter changes with URL sync
  const handleStatusFilterChange = (values) => {
    const newFilter = values?.length
      ? [values[0]]
      : [{ value: null, label: "All Status" }]

    setStatusFilter(newFilter)
    setRefreshKey((prev) => prev + 1) // Refresh the DataTable

    // Update URL with new status filter
    const currentParams = Object.fromEntries(urlParams.entries())
    const statusValue = newFilter[0]?.value
    if (statusValue) {
      updateURL({ ...currentParams, status: statusValue, page: undefined }) // Reset page when filter changes
    } else {
      // Remove status from URL when "All Status" is selected
      const { status, page, ...otherParams } = currentParams
      updateURL(otherParams)
    }
  }

  // Sync state with URL parameters on initial load
  useEffect(() => {
    if (typeof window !== "undefined") {
      const currentUrlParams = new URLSearchParams(window.location.search)
      setUrlParams(currentUrlParams)

      // Sync activeTab
      const tabParam = currentUrlParams.get('tab')
      const validTabs = ['details', 'licenses']
      
      if (tabParam && validTabs.includes(tabParam)) {
        setActiveTab(tabParam)
      }

      // Sync status filter
      const statusParam = currentUrlParams.get('status')
      if (statusParam) {
        const statusOptions = [
          { value: null, label: "All Status" },
          { value: "free", label: "Free" },
          { value: "active", label: "Active" },
          { value: "paused", label: "Paused" },
          { value: "expired", label: "Expired" },
          { value: "revoked", label: "Revoked" },
        ]
        const matchingStatus = statusOptions.find(opt => opt.value === statusParam)
        if (matchingStatus) {
          setStatusFilter([matchingStatus])
        }
      }
    }
  }, [packId])  // Add packId dependency so it syncs when packId changes

  // Update URL when state changes
  const updateURL = (newParams) => {
    if (typeof window !== "undefined") {
      const url = new URL(window.location)

      // Clear existing search params and set new ones
      url.search = ''
      Object.entries(newParams).forEach(([key, value]) => {
        if (value && value !== '' && value !== null && value !== undefined) {
          url.searchParams.set(key, value)
        }
      })

      window.history.replaceState({}, '', url.toString())
      setUrlParams(new URLSearchParams(url.search))
    }
  }

  // Handle tab changes with URL sync
  const handleTabChange = (newTab) => {
    setActiveTab(newTab)
    const currentParams = Object.fromEntries(urlParams.entries())
    updateURL({ ...currentParams, tab: newTab })
  }

  // Monitor URL changes and auto-close if URL doesn't match expected pattern
  useEffect(() => {
    const handlePopState = () => {
      if (typeof window !== "undefined") {
        const pathname = window.location.pathname
        const packMatch = pathname.match(/\/product-licensing\/packs\/([^\/]+)/)

        // If URL doesn't match /product-licensing/packs/id pattern, close viewer
        if (!packMatch || packMatch[1] !== packId) {
          onClose()
        }
      }
    }

    // Listen for navigation events
    window.addEventListener('popstate', handlePopState)

    return () => {
      window.removeEventListener('popstate', handlePopState)
    }
  }, [packId, onClose])

  // Load pack details
  const loadPackDetails = async () => {
    try {
      setLoading(true)

      const response = await GetPackDetails(packId)

      if (response.result) {
        // FIX: The backend returns the pack data under 'pack', not 'data'
        const packData = response.pack || response.data
        setPack(packData)
      } else {
        showNotification({
          message:
            response.error || response.message || "Failed to load pack details",
          className: "bg-admin_error text-white",
        })
      }
    } catch (error) {
      showNotification({
        message: "Error loading pack details: " + error.message,
        className: "bg-admin_error text-white",
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (packId) {
      loadPackDetails()
    }
  }, [packId, refreshKey])


  // Handle pack state toggle
  const handleToggleState = async () => {
    if (!pack) return

    const newState = pack.state === "active" ? "paused" : "active"
    const action = newState === "active" ? "activate" : "pause"

    showPromptDialog({
      title: `${action.charAt(0).toUpperCase() + action.slice(1)} Pack`,
      message: `Are you sure you want to ${action} "${pack.name}"?`,
      confirmText: action.charAt(0).toUpperCase() + action.slice(1),
      confirmVariant: newState === "active" ? "primary" : "warning",
      onConfirm: async () => {
        try {
          const response = await UpdatePack({
            packId: pack._id,
            state: newState,
          })

          if (response.result) {
            showNotification({
              message: `Pack ${action}d successfully`,
              className: "bg-admin_primary text-white",
            })
            setRefreshKey((prev) => prev + 1)
          } else {
            showNotification({
              message: response.error || `Failed to ${action} pack`,
              className: "bg-admin_error text-white",
            })
          }
        } catch (error) {
          showNotification({
            message: `An error occurred while ${action}ing pack`,
            className: "bg-admin_error text-white",
          })
        }
      },
    })
  }

  // Handle pack deletion
  const handleDeletePack = () => {
    if (!pack) return

    // Check if pack is primary
    if (pack.primary) {
      showNotification({
        message: `The pack "${pack.name}" is primary. Remove its primary status before deletion.`,
        className: "bg-admin_warning text-white",
      })
      return
    }

    showPromptDialog({
      title: "Delete Pack",
      message: `Are you sure you want to delete "${pack.name}"? This action cannot be undone and will affect all licenses in this pack.`,
      confirmText: "Delete Pack",
      confirmVariant: "error_m",
      onConfirm: async () => {
        try {
          const response = await DeletePack(pack._id)

          if (response.result) {
            showNotification({
              message: "Pack deleted successfully",
              className: "bg-admin_primary text-white",
            })
            navigate("/admin/product-licensing/packs") // Navigate back to pack list
          } else {
            showNotification({
              message: response.message || response.error || "Failed to delete pack",
              className: "bg-admin_error text-white",
            })
          }
        } catch (error) {
          // Extract error message from different possible sources
          let errorMessage = "An error occurred while deleting pack"

          // Check if it's an axios error with response data
          if (error.response && error.response.data) {
            const responseData = error.response.data
            errorMessage = responseData.message || responseData.error || error.message
          } else if (error.message) {
            errorMessage = error.message
          }

          showNotification({
            message: errorMessage,
            className: "bg-admin_error text-white",
          })
        }
      },
    })
  }

  // Navigate to license generator with this pack pre-selected
  const handleGenerateLicenses = () => {
    navigate(`/admin/product-licensing/licenses/generate?packId=${packId}`)
  }

  // Handle select all licenses
  const handleSelectAllLicenses = (items, isSelected) => {
    if (isSelected) {
      setSelectedLicenses(items.map((item) => item._id))
    } else {
      setSelectedLicenses([])
    }
  }

  // Handle individual license selection
  const handleSelectLicense = (licenseId, isSelected) => {
    if (isSelected) {
      setSelectedLicenses((prev) => [...prev, licenseId])
    } else {
      setSelectedLicenses((prev) => prev.filter((id) => id !== licenseId))
    }
  }

  // Handle bulk delete licenses
  const handleDeleteSelectedLicenses = () => {
    if (selectedLicenses.length === 0) return

    showPromptDialog({
      title: "Delete Licenses",
      message: `Are you sure you want to delete ${
        selectedLicenses.length
      } license${
        selectedLicenses.length === 1 ? "" : "s"
      } from this pack? This action cannot be undone.`,
      confirmText: "Delete",
      confirmVariant: "error_m",
      onConfirm: async () => {
        try {
          const response = await DeleteLicenses(selectedLicenses)

          if (response.result) {
            showNotification({
              message: `${response.deleted} license${
                response.deleted === 1 ? "" : "s"
              } deleted successfully`,
              className: "bg-admin_primary text-white",
            })
            setSelectedLicenses([])
            setRefreshKey((prev) => prev + 1)
          } else {
            showNotification({
              message: response.error || "Failed to delete licenses",
              className: "bg-admin_error text-white",
            })
          }
        } catch (error) {
          showNotification({
            message: "An error occurred while deleting licenses",
            className: "bg-admin_error text-white",
          })
        }
      },
    })
  }

  // Handle export pack licenses
  const handleExportPackLicenses = () => {
    LicenseExportDialog.exportPackLicenses(
      packId,
      pack?.name,
      getLicenseRequestFields()
    )
  }

  // Handle primary toggle
  const handlePrimaryToggle = async (isPrimary) => {
    // If setting to primary, show confirmation dialog
    if (isPrimary) {
      showPrimaryPackConfirmDialog(async () => {
        await performPrimaryToggle(isPrimary)
      })
    } else {
      // If removing primary, proceed directly
      await performPrimaryToggle(isPrimary)
    }
  }

  // Extracted function to perform the actual primary toggle
  const performPrimaryToggle = async (isPrimary) => {
    try {
      const response = await UpdatePack({
        packId: pack._id,
        primary: isPrimary,
      })

      if (response.result) {
        showNotification({
          message: `Pack ${isPrimary ? "set as primary" : "removed as primary"}`,
          className: "bg-admin_primary text-white",
        })
        setRefreshKey((prev) => prev + 1)
      } else {
        showNotification({
          message: response.message || response.error || "Failed to update pack primary status",
          className: "bg-admin_error text-white",
        })
      }
    } catch (error) {
      // Extract error message from different possible sources
      let errorMessage = "An error occurred while updating pack primary status"

      // Check if it's an axios error with response data
      if (error.response && error.response.data) {
        const responseData = error.response.data
        
        // Handle specific error cases
        if (responseData.error === "last_primary_pack") {
          errorMessage = responseData.message || "Cannot remove primary status from the last primary pack. At least one pack must remain as primary."
        } else {
          errorMessage = responseData.message || responseData.error || error.message
        }
      } else if (error.message) {
        errorMessage = error.message
      }

      showNotification({
        message: errorMessage,
        className: "bg-admin_error text-white",
      })
    }
  }

  // License table columns for the Licenses tab
  const getLicenseColumns = () => {
    const baseColumns = [
    {
      id: "code",
      title: "License Code",
      headerClassName: "w-[200px]",
      render: (_, item) => (
        <div
          className="cursor-pointer hover:text-admin_primary"
          onClick={() =>
            navigate(`/admin/product-licensing/licenses/${item._id}`)
          }
        >
          <div className="flex items-center space-x-2">
            <Typography 
              variant="admin_txt1" 
              className="font-mono font-medium"
            >
              {item.code}
            </Typography>
            
            {/* Show external badge if pack is external (licenses inherit pack's external status) */}
            {pack?.external && (
              <Typography 
                variant="admin_txt2"
                className="text-white font-medium bg-violet-600 rounded-full px-2 py-1 text-xs"
              >
                external
              </Typography>
            )}
          </div>
        </div>
      ),
    },
    {
      id: "status",
      title: "Status",
      headerClassName: "w-[100px]",
      render: (_, item) => {
        const statusConfig = {
          free: {
            color: "bg-admin_success text-white",
            label: "Free",
          },
          active: {
            color: "bg-admin_primary text-white",
            label: "Active",
          },
          paused: {
            color: "bg-admin_warning text-white",
            label: "Paused",
          },
          expired: {
            color: "bg-admin_error text-white",
            label: "Expired",
          },
          revoked: {
            color: "bg-admin_neutral_1 text-admin_text",
            label: "Revoked",
          },
        }
        const config = statusConfig[item.status] || statusConfig.free

        return (
          <Typography
            variant="admin_txt2"
            className={twMerge(
              "text-xs font-medium rounded-full px-3 py-1",
              config.color
            )}
          >
            {config.label}
          </Typography>
        )
      },
    },
    {
      id: "owner",
      title: "Owner",
      headerClassName: "w-[180px]",
      render: (_, item) => (
        <Typography 
          variant="admin_txt1" 
          className="text-admin_text"
        >
          {item.owner ? (
            <>
              {item.owner.name || item.owner.email}
              {item.owner.name && (
                <div>
                  <Typography
                    variant="admin_txt2_dim"
                  >
                    {item.owner.email}
                  </Typography>
                </div>
              )}
            </>
          ) : (
            <span className="text-admin_text_secondary">
              Unassigned
            </span>
          )}
        </Typography>
      ),
    },
  ]

    // Add seats column only if pack is not external
    if (!pack?.external) {
      const seatsColumn = {
        id: "seats",
        title: "Seats",
        headerClassName: "w-[120px]",
        render: (_, item) => (
          <div className="text-center">
            <Typography variant="admin_body">
              {item.usedSeats || 0} / {item.seats}
            </Typography>
            
            <div className="w-full h-2 bg-admin_neutral_1 rounded-full mt-1">
              <div
                className="h-2 bg-admin_primary rounded-full transition-all"
                style={{ width: `${item.seatUsage || 0}%` }}
              />
            </div>
          </div>
        ),
      }
      // Add seats column after owner
      baseColumns.push(seatsColumn)
    }

    return baseColumns
  }

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center p-6">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-admin_primary"></div>
      </div>
    )
  }

  if (!pack) {
    return (
      <div className="h-full flex items-center justify-center p-6">
        <div className="text-center">
          <Typography 
            variant="admin_body" 
            className="text-admin_error mb-4"
          >
            Pack not found
          </Typography>

          <Typography
            variant="admin_txt1_dim"
            className="mb-4"
          >
            Pack ID: {packId}
          </Typography>
        </div>
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col bg-admin_canvas">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 border-b border-admin_bordered bg-admin_paper p-6">
        <div className="flex items-start space-x-4 -mt-2">
          {/* Pack name and description */}
          <div>
            <Typography variant="admin_h3">
              {pack.name}
            </Typography>

            {pack.description && (
              <Typography
                variant="admin_txt1_dim"
              >
                {pack.description}
              </Typography>
            )}
          </div>

          {/* Pack state and primary status */}
          <div className="flex items-center space-x-2 mt-2">
            <span
              className={twMerge(
                "px-3 py-1 rounded-full text-xs font-medium",
                pack.state === "active"
                  ? "bg-admin_success text-white"
                  : "bg-admin_warning text-white"
              )}
            >
              {pack.state}
            </span>

            {pack.primary && (
              <Typography 
                variant="admin_txt2"
                className="text-admin_text_inv font-medium bg-admin_progress rounded-full px-3 py-1"
              >
                primary
              </Typography>
            )}
          </div>
        </div>

        {/* Right side: Action buttons */}
        <div className="flex items-center space-x-3">
          <Button
            variant="admin_primary"
            className="flex items-center"
            onClick={handleGenerateLicenses}
          >
            <PlusCircleIcon className="w-5 h-5 mr-2" />
            Add
          </Button>

          <Button
            variant="admin_secondary"
            className="flex items-center"
            onClick={handleExportPackLicenses}
          >
            <ArrowDownTrayIcon className="w-5 h-5 mr-2" />
            Export
          </Button>

          <Button 
            variant="admin_secondary" 
            onClick={() => setEditDialog(true)}
          >
            <PencilIcon className="w-5 h-5 mr-2" />
            Edit
          </Button>

          <Button 
            variant="admin_secondary" 
            onClick={handleToggleState}
          >
            {pack.state === "active" ? (
              <>
                <PauseIcon className="w-5 h-5 mr-2" />
                Pause
              </>
            ) : (
              <>
                <PlayIcon className="w-5 h-5 mr-2" />
                Activate
              </>
            )}
          </Button>

          <Button 
            variant="admin_error" 
            onClick={handleDeletePack}
          >
            <TrashIcon className="w-5 h-5" />
          </Button>
        </div>
      </div>

      {/* Content Tabs */}
      <div className="flex-1 p-6">
        <Tabs
          variant="admin"
          value={activeTab}
          onChange={handleTabChange}
          tabs={[
            { 
              value: "details", 
              label: (
                <div className="flex items-center space-x-2">
                  <ClipboardDocumentListIcon className="w-4 h-4" />
                  <span>Details</span>
                </div>
              )
            },
            { 
              value: "licenses", 
              label: (
                <div className="flex items-center space-x-2">
                  <DocumentTextIcon className="w-4 h-4" />
                  <span>Licenses</span>
                </div>
              )
            },
          ]}
          className="mb-6"
        />

        {/* Tab Content */}
        {activeTab === "details" && (
          <div className="space-y-6">
            {/* Pack Statistics */}
            <div className={`grid grid-cols-1 gap-6 ${pack.external ? 'md:grid-cols-2' : 'md:grid-cols-4'}`}>
              {(() => {
                const allStatisticsCards = [
                  {
                    value: pack.statistics?.licenses || 0,
                    label: "Total Licenses",
                    colorClass: "text-admin_success",
                    suffix: ""
                  },
                  {
                    value: pack.statistics?.seats || 0,
                    label: "Total Seats",
                    colorClass: "text-admin_primary",
                    suffix: "",
                    hideForExternal: true
                  },
                  {
                    value: pack.statistics?.active || 0,
                    label: "Active Licenses",
                    colorClass: "text-admin_warning",
                    suffix: ""
                  },
                  {
                    value: pack.statistics?.seatUsage || 0,
                    label: "Seat Usage",
                    colorClass: "text-admin_progress",
                    suffix: "%",
                    hideForExternal: true
                  }
                ];

                // Filter out seat-related cards for external packs
                const statisticsCards = pack.external 
                  ? allStatisticsCards.filter(card => !card.hideForExternal)
                  : allStatisticsCards;

                return statisticsCards.map((card, index) => (
                  <div key={index} className="bg-admin_paper rounded-xl p-6">
                    <Typography 
                      component="output"
                      variant="admin_h3" 
                      className={card.colorClass}
                    >
                      {card.value}{card.suffix}
                    </Typography>

                    <Typography
                      variant="admin_label"
                      className="my-0"
                    >
                      {card.label}
                    </Typography>
                  </div>
                ));
              })()}
            </div>

            {/* Pack Information */}
            <div className="bg-admin_paper rounded-xl p-6">
              <Typography 
                component="h2"
                variant="admin_h4" 
                className="mb-6"
              >
                Pack Information
              </Typography>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 xl:gap-18">
                <div className="space-y-6">
                  <div>
                    <Typography 
                      variant="admin_label"
                      className="my-0"
                    >
                      Pack ID:
                    </Typography>

                    <Typography 
                      variant="admin_txt1_dim" 
                      className="font-mono"
                    >
                      {pack._id}
                    </Typography>
                  </div>

                  <div>
                    <Typography
                      variant="admin_label"
                      className="my-0"
                    >
                      Created Date:
                    </Typography>

                    <Typography variant="admin_txt1_dim">
                      {new Date(pack.created).toLocaleDateString()} at{" "}
                      {new Date(pack.created).toLocaleTimeString()}
                    </Typography>
                  </div>

                  {/* Primary Status - Only show for non-external packs */}
                  {!pack.external && (
                    <div>
                      <Typography 
                        variant="admin_label" 
                        className="my-0"
                      >
                        Primary Status
                      </Typography>

                      <div className="flex flex-col gap-2 mt-2">
                        <Typography
                          variant="admin_txt2_dim"
                          className="flex-1 mr-4 leading-tight"
                        >
                          {pack.primary
                            ? "This is the primary pack. All system-generated licenses will be placed here by default."
                            : "This pack is not primary. Setting it as primary will direct all system license generation to this pack."}
                        </Typography>

                        <div className="flex items-center gap-2">
                          <ToggleSwitch
                            variant="admin"
                            isOn={pack.primary || false}
                            onChange={handlePrimaryToggle}
                          />
                          
                          <Typography 
                            variant="admin_txt1"
                            className="text-admin_text whitespace-nowrap"
                          >
                            Set as Primary
                          </Typography>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="space-y-6">
                  <div>
                    <Typography 
                      variant="admin_label"
                      className="my-0"
                    >
                      Current State:
                    </Typography>

                    <Typography variant="admin_txt1_dim">
                      {pack.state === "active"
                        ? "Active (licenses can be generated)"
                        : "Paused (license generation disabled)"}
                    </Typography>
                  </div>

                  <div>
                    <Typography 
                      variant="admin_label"
                      className="my-0"
                    >
                      Pack Type:
                    </Typography>

                    <div className="flex items-center space-x-2">
                      <Typography variant="admin_txt1_dim">
                        {pack.external ? "External Pack" : "Internal Pack"}
                      </Typography>

                      {pack.external && (
                        <Typography 
                          variant="admin_txt2"
                          className="text-white font-medium bg-violet-600 rounded-full px-3 py-1"
                        >
                          external
                        </Typography>
                      )}
                    </div>

                    {pack.external && (
                      <Typography variant="admin_txt2_dim" className="mt-1">
                        For partner products or external systems. Activation tracking is handled externally.
                      </Typography>
                    )}
                  </div>

                  <div>
                    <Typography 
                      variant="admin_label"
                      className="my-0"
                    >
                      Last Updated:
                    </Typography>

                    <Typography variant="admin_txt1_dim">
                      {pack.updated
                        ? new Date(pack.updated).toLocaleDateString()
                        : "Never"}
                    </Typography>
                  </div>
                </div>
              </div>
            </div>

          </div>
        )}

        {activeTab === "licenses" && (
          <div>
            <DataTable
              localUserPreferencesKey="admin.licensing.packViewer"
              variant="admin_browser"
              title="Pack Licenses"
              apiMethod={GetLicenses}
              requestFields={getLicenseRequestFields()}
              searchObject={(() => {
                const search = {}
                const searchParam = urlParams.get('search')
                if (searchParam) {
                  search['code owner.email owner.name'] = searchParam
                }
                return search
              })()}
              headerControls={
                <div className="w-full flex flex-row items-end gap-4 mt-5">
                  <div className="flex flex-col">
                    <Typography
                      variant="admin_txt2_dim"
                      className="mb-0.5"
                    >
                      Status
                    </Typography>

                    <DropDownSelect
                      variant="admin"
                      options={[
                        { value: null, label: "All Status" },
                        { value: "free", label: "Free" },
                        { value: "active", label: "Active" },
                        { value: "paused", label: "Paused" },
                        { value: "expired", label: "Expired" },
                        { value: "revoked", label: "Revoked" },
                      ]}
                      value={statusFilter}
                      onChange={handleStatusFilterChange}
                      showCheckbox={false}
                      className="w-[180px]"
                    />
                  </div>

                  {/* User filter */}
                  <div className="flex flex-col">
                    <Typography 
                      variant="admin_txt2_dim"
                      className="mb-0.5"
                    >
                      Owner
                    </Typography>

                    <DropDownSelect
                      variant="admin"
                      searchEnabled
                      useExternalSearch
                      enableResetButton
                      showCheckbox={false}
                      apiMethod={async (params) => {
                        if (!params.searchTerm || params.searchTerm.length < 2) {
                          return { data: [] }
                        }

                        const response = await UserPlatformAPI.SearchUsers(params.searchTerm)
                        return response
                      }}
                      onDataLoaded={(rawData) => {
                        return rawData.map(user => ({
                          _id: user._id,
                          name: user.name || 'Unknown User',
                          email: user.email || 'No email',
                          value: user._id,
                          label: `${user.name || 'Unknown User'} (${user.email || 'No email'})`,
                          ...user
                        }))
                      }}
                      searchField="name email"
                      valueField="_id"
                      nameField="label"
                      className="w-[280px]"
                      placeholder="Filter by owner"
                      noDataMessage="Filter by owner"
                      value={userFilter}
                      onChange={(value) => {
                        setUserFilter(value)
                        setRefreshKey((prev) => prev + 1)
                      }}
                      onValueItemRender={(user) => {
                        if (!user) return "Filter by owner"
                        return (
                          <div className="flex items-center gap-3">
                            <div className="w-6 h-6 rounded-full bg-admin_primary flex items-center justify-center flex-shrink-0">
                              {user.avatar ? (
                                <img src={url(user.avatar)} alt={user.name} className="w-6 h-6 rounded-full object-cover" />
                              ) : (
                                <span className="text-white font-medium text-xs">
                                  {(user.name || user.email || 'U').charAt(0).toUpperCase()}
                                </span>
                              )}
                            </div>
                            
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-sm text-admin_text truncate">
                                {user.name || 'Unknown User'}
                              </div>
                              <div className="text-xs text-admin_text_secondary truncate">
                                {user.email || 'No email'}
                              </div>
                            </div>
                          </div>
                        )
                      }}
                      onItemRender={(user) => (
                        <div className="flex items-center gap-3 p-2">
                          <div className="w-8 h-8 rounded-full bg-admin_primary flex items-center justify-center flex-shrink-0">
                            {user.avatar ? (
                              <img src={url(user.avatar)} alt={user.name} className="w-8 h-8 rounded-full object-cover" />
                            ) : (
                              <span className="text-white font-medium text-sm">
                                {(user.name || user.email || 'U').charAt(0).toUpperCase()}
                              </span>
                            )}
                          </div>
                          
                          <div className="flex-1">
                            <div className="font-medium text-sm text-gray-900">
                              {user.name || 'Unknown User'}
                            </div>
                            <div className="text-xs text-gray-500">
                              {user.email || 'No email'}
                            </div>
                          </div>
                        </div>
                      )}
                    />
                  </div>
                </div>
              }
              enableSelection={true}
              selectedItems={selectedLicenses}
              onSelectionChange={(ids) => setSelectedLicenses(ids)}
              bulkActions={[
                {
                  label: "Delete",
                  variant: "admin_error",
                  icon: <TrashIcon className="w-5 h-5" />,
                  onClick: handleDeleteSelectedLicenses,
                }
              ]}
              itemIdField="_id"
              searchField="code owner.email owner.name"
              sort={{ created: -1 }}
              renders={getLicenseColumns()}
              refreshKey={refreshKey}
              preferredHeight="65vh"
              itemHeight="h-16"
              noData={
                <div className="text-center py-8">
                  <ChartBarIcon className="w-12 h-12 text-admin_text_secondary mx-auto mb-4" />

                  <Typography variant="admin_body_dim">
                    No licenses in this pack yet
                  </Typography>

                  <Button
                    variant="admin_primary"
                    className="mt-4"
                    onClick={handleGenerateLicenses}
                  >
                    Generate First Licenses
                  </Button>
                </div>
              }
              styleOverrides={{
                table: "w-full table-fixed",
                getRowStyle: (_, item) => {
                  if (item && selectedLicenses.includes(item._id)) {
                    return "bg-admin_primary/10 hover:bg-admin_primary/20"
                  }
                },
              }}
            />
          </div>
        )}

      </div>

      {/* Edit Pack Dialog */}
      <AddPackDialog
        isOpen={editDialog}
        pack={pack}
        onClose={() => setEditDialog(false)}
        onPackSaved={() => {
          setEditDialog(false)
          setRefreshKey((prev) => prev + 1)
        }}
      />
    </div>
  )
}
